<template>
  <!-- NeuroStandardsTimeline.vue -->
  <main class="neuro-vertical">
    <!-- Header -->
    <header class="neuro-header">
      <div class="neuro-title">神经发育 · 月龄时间线</div>
      <div class="neuro-sub">按月龄展示各发育领域的项目、标准与教具</div>
    </header>

    <!-- Loading状态 -->
    <div v-if="loading" class="loading">
      <p>正在加载...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="err" class="error">
      <p>{{ err }}</p>
      <button @click="fetchAll" class="retry-btn">重新加载</button>
    </div>

    <!-- Vertical timeline -->
    <section v-else class="neuro-timeline" ref="timelineEl">
      <article
        v-for="(m, i) in filteredMonths"
        :key="m"
        :id="'month-' + m"
        class="neuro-item"
        :class="{ 'is-active': activeMonth === m }"
      >
        <!-- Left rail: dot + line -->
        <div class="neuro-rail" aria-hidden="true">
          <span class="neuro-dot"></span>
          <span class="neuro-line"></span>
        </div>

        <!-- Right card -->
        <div class="neuro-card" :class="{ 'has-half-overlay': shouldShowHalfPaywall(m) }">
          <header class="neuro-card-head">
            <div class="neuro-badge" :class="`c${(i % 5) + 1}`">{{ m }} 月龄</div>
          </header>

          <!-- Domains -->
          <div 
            v-for="d in getFilteredDomains(m)" 
            :key="m + '-' + d" 
            class="neuro-domain"
          >
            <button class="neuro-domain-toggle" @click="toggle(m + '-' + d)">
              <!-- 直接使用内联样式显示领域标签 -->
              <span 
                class="domain-badge"
                :style="getDomainStyle(d)"
              >
                {{ d }}
              </span>
              <span class="neuro-count">共 {{ byMonth[m][d].length }} 项</span>
              <span class="neuro-chevron" :class="{ on: opened[m + '-' + d] }">▾</span>
            </button>
            
            <ul class="neuro-items-list" v-show="opened[m + '-' + d]">
              <li v-for="it in byMonth[m][d]" :key="it.id || it.item" class="neuro-item-detail">
                <div class="neuro-item-title">
                  {{ it.item }}
                  <span v-if="it.warning_flag === 1" class="neuro-warning">⚠ 警示</span>
                </div>
                <div class="neuro-item-standard">{{ it.standard }}</div>
                <div v-if="it.tool" class="neuro-item-tool">教具：{{ it.tool }}</div>
              </li>
            </ul>
          </div>

          <!-- initialMonth 显示半覆盖蒙层 + 按钮 -->
          <div v-if="shouldShowHalfPaywall(m)" class="neuro-half-overlay">
            <p>开通VIP服务后查看完整内容。</p>
            <button @click="goToPaymentPage" class="upgrade-btn">立即升级</button>
          </div>

          <!-- initialMonth + 1 显示完整蒙层 + 按钮 -->
          <div v-if="shouldShowPaywall(m)" class="neuro-overlay">

          </div>
        </div>
      </article>
    </section>
  </main>
</template>

<script>
export default {
    name: 'NeuroStandardsTimeline',
    props: {
        apiBase: { type: String, default: '/api/neurostandards' },
        months: { type: Array, default: function () { return [1, 2, 3, 4, 5, 6]; } },
        domainOrder: {
            type: Array,
            default: function () {
                return ['大运动', '精细动作', '适应能力', '语言', '社会行为', '交流互动警示行为'];
            }
        },
        // 新增：允许外部传入初始月龄
        initialMonth: { type: Number, default: null },
        // 新增：是否已付费
        isPaid: { type: Boolean, default: true }
    },
    data () {
        return {
            loading: false,
            err: null,
            rows: [],
            byMonth: {},
            opened: {},
            activeMonth: null,
            io: null
        };
    },
    computed: {
        // 新增：根据付费状态过滤月龄数组
        filteredMonths () {
            if (this.isPaid) {
                // 已付费，显示所有月龄
                return this.months;
            } else if (this.initialMonth) {
                // 未付费，只显示到 initialMonth + 1
                return this.months.filter(m => m <= this.initialMonth + 1);
            } else {
                // 没有 initialMonth，显示所有
                return this.months;
            }
        }
    },
    mounted () {
        this.fetchAll();
    },
    methods: {
        getDomainStyle (domain) {
            var styleMap = {
                '大运动': { 
                    background: '#eff6ff', 
                    color: '#1d4ed8', 
                    borderColor: '#bfdbfe' 
                },
                '精细动作': { 
                    background: '#ecfdf5', 
                    color: '#047857', 
                    borderColor: '#a7f3d0' 
                },
                '适应能力': { 
                    background: '#fffbeb', 
                    color: '#b45309', 
                    borderColor: '#fde68a' 
                },
                '语言': { 
                    background: '#f5f3ff', 
                    color: '#6d28d9', 
                    borderColor: '#ddd6fe' 
                },
                '社会行为': { 
                    background: '#ecfeff', 
                    color: '#0e7490', 
                    borderColor: '#a5f3fc' 
                },
                '交流互动警示行为': { 
                    background: '#fff1f2', 
                    color: '#be123c', 
                    borderColor: '#fecdd3' 
                }
            };
            return styleMap[domain] || { 
                background: '#f9fafb', 
                color: '#374151', 
                borderColor: '#e5e7eb' 
            };
        },
        
        getFilteredDomains (month) {
            return this.domainOrder.filter(domain => {
                return this.byMonth[month] && 
                       this.byMonth[month][domain] && 
                       this.byMonth[month][domain].length > 0;
            });
        },
        
        // 修改：判断是否显示完整付费墙
        shouldShowPaywall (month) {
            if (this.isPaid) {
                return false;
            }
            
            // 只在 initialMonth + 1 显示完整付费墙
            if (this.initialMonth && month === this.initialMonth + 1) {
                return true;
            }
            
            return false;
        },
        
        // 新增：判断是否显示半覆盖蒙层
        shouldShowHalfPaywall (month) {
            if (this.isPaid) {
                return false;
            }
            
            // 在 initialMonth 显示半覆盖
            if (this.initialMonth && month === this.initialMonth) {
                return true;
            }
            
            return false;
        },
        
        // 新增：跳转到支付页面
        goToPaymentPage () {
            window.location.href = 'babycare://payment';
        },
        
        async fetchAll () {
            this.loading = true;
            this.err = null;
            try {
                var monthsParam = this.months.join(',');
                var url = this.apiBase + '?age_month=' + encodeURIComponent(monthsParam) + '&page=1&page_size=1000';
                var res = await fetch(url);
                if (res.ok) {
                    var json = await res.json();
                    this.rows = (json.result || []);
                } else {
                    this.rows = [];
                    for (var i = 0; i < this.months.length; i++) {
                        var m = this.months[i];
                        var r = await fetch(this.apiBase + '?age_month=' + m + '&page=1&page_size=1000');
                        if (!r.ok) {throw new Error('HTTP ' + r.status);}
                        var j = await r.json();
                        this.rows = this.rows.concat(j.result || []);
                    }
                }
                this.groupByMonth();
                
                // 初始化所有领域为展开状态（使用 filteredMonths）
                this.filteredMonths.forEach(month => {
                    this.getFilteredDomains(month).forEach(domain => {
                        this.$set(this.opened, month + '-' + domain, true);
                    });
                });
                
                // 等待 DOM 更新后再执行
                this.$nextTick(() => {
                    // 先设置 IntersectionObserver
                    this.setupIntersectionObserver();
                    
                    // 延迟更长时间，确保 DOM 完全渲染
                    setTimeout(() => {
                        this.scrollToInitialMonth();
                    }, 500); // 增加到 500ms
                });
            } catch (e) {
                this.err = (e && e.message) || '加载失败';
            } finally {
                this.loading = false;
            }
        },
        
        groupByMonth () {
            var map = {};
            for (var i = 0; i < this.rows.length; i++) {
                var r = this.rows[i];
                var m = Number(r.age_month);
                
                // 如果未付费，只显示到 initialMonth + 1 的数据
                if (!this.isPaid && this.initialMonth && m > this.initialMonth + 1) {
                    continue; // 跳过超出范围的月龄数据
                }
                
                if (!map[m]) {map[m] = {};}
                if (!map[m][r.domain]) {map[m][r.domain] = [];}
                map[m][r.domain].push(r);
            }
            this.byMonth = map;
        },
        
        toggle (key) {
            this.$set(this.opened, key, !this.opened[key]);
        },
        
        buildThresholdList () {
            const thresholds = [];
            const numSteps = 20;
            for (let i = 0; i <= numSteps; i++) {
                thresholds.push(i / numSteps);
            }
            return thresholds;
        },
        
        setupIntersectionObserver () {
            if (this.io) {
                this.io.disconnect();
            }

            this.io = new IntersectionObserver(
                (entries) => {
                    let best = { ratio: 0, month: null };
                    entries.forEach((e) => {
                        if (e.isIntersecting && e.intersectionRatio > best.ratio) {
                            const id = e.target.id;
                            const month = parseInt(id.replace('month-', ''));
                            best = { ratio: e.intersectionRatio, month };
                        }
                    });
                    if (best.month !== null) {
                        this.activeMonth = best.month;
                    }
                },
                { root: null, rootMargin: "-20% 0px -55% 0px", threshold: this.buildThresholdList() }
            );

            // 使用 filteredMonths 而不是 months
            this.filteredMonths.forEach((m) => {
                const el = document.getElementById('month-' + m);
                if (el) {
                    this.io.observe(el);
                }
            });
        },
        
        // 修改：滚动到初始月龄
        scrollToInitialMonth () {
            const targetMonth = this.initialMonth;
            
            console.log('尝试滚动到月龄:', targetMonth);
            
            if (!targetMonth) {
                console.log('没有指定 initialMonth');
                return;
            }
            
            // 使用 filteredMonths 检查
            if (this.filteredMonths.includes(targetMonth)) {
                // 多次尝试查找元素，确保 DOM 已渲染
                let attempts = 0;
                const maxAttempts = 10;
                
                const tryScroll = () => {
                    const el = document.getElementById('month-' + targetMonth);
                    console.log('查找元素 #month-' + targetMonth + ':', el, '尝试次数:', attempts + 1);
                    
                    if (el) {
                        // 设置激活状态
                        this.activeMonth = targetMonth;
                        
                        // 使用 scrollIntoView 实现滚动
                        setTimeout(() => {
                            console.log('开始滚动到月龄:', targetMonth);
                            
                            el.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'start',
                                inline: 'nearest'
                            });
                            
                            console.log('滚动命令已执行');
                        }, 100);
                    } else if (attempts < maxAttempts) {
                        attempts++;
                        setTimeout(tryScroll, 100);
                    } else {
                        console.log('找不到元素，已达到最大尝试次数');
                    }
                };
                
                tryScroll();
            } else {
                console.log('月龄不在 filteredMonths 中');
            }
        }
    },
    
    beforeDestroy () {
        if (this.io) {
            this.io.disconnect();
        }
    }
};
</script>

<style scoped>
/* 原有样式保持不变 */
:root {
  --ink: #1f2937;
  --muted: #6b7280;
  --line: #e5e7eb;
  --card: #fff;
  --accent: #05C2CB;
}

.neuro-vertical {
  max-width: 880px;
  margin: 0 auto;
  padding: 18px 14px 60px;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "PingFang SC", "Noto Sans CJK SC", "Microsoft YaHei", sans-serif;
  color: var(--ink);
}

/* Header */
.neuro-header {
  text-align: center;
  margin-bottom: 12px;
}
.neuro-title {
  font-weight: 800;
  font-size: 22px;
}
.neuro-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

/* Loading & Error */
.loading, .error {
  text-align: center;
  padding: 40px 20px;
}
.loading p {
  font-size: 16px;
  color: var(--muted);
}
.error p {
  font-size: 16px;
  color: #ef4444;
  margin-bottom: 16px;
}
.retry-btn {
  background: var(--accent);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}
.retry-btn:hover {
  opacity: 0.9;
}

/* Timeline */
.neuro-timeline {
  position: relative;
}
.neuro-item {
  position: relative;
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 10px;
  padding: 10px 0;
  scroll-margin-top: 80px;
}
.neuro-item.is-active .neuro-dot {
  transform: scale(1.15);
  box-shadow: 0 0 0 4px rgba(5, 194, 203, 0.2);
}
.neuro-rail {
  position: relative;
  display: grid;
  justify-items: center;
  margin-top: 16px;
}
.neuro-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: var(--accent);
  border: 2px solid var(--accent);
  transition: transform 0.2s ease;
  z-index: 1;
}
.neuro-line {
  position: absolute;
  top: 14px;
  bottom: -10px;
  width: 2px;
  background: var(--accent);
  border-radius: 2px;
}

/* Card */
.neuro-card {
  position: relative; /* 为蒙层定位做准备 */
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .04);
}
.neuro-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.neuro-badge {
  padding: 6px 10px;
  border-radius: 8px;
  color: #fff;
  font-weight: 800;
  font-size: 12px;
}
.neuro-badge.c1 { background: #0ea5e9; }
.neuro-badge.c2 { background: #10b981; }
.neuro-badge.c3 { background: #f59e0b; }
.neuro-badge.c4 { background: #8b5cf6; }
.neuro-badge.c5 { background: #ef4444; }

/* Domain */
.neuro-domain {
  margin-top: 10px;
}
.neuro-domain-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line);
  background: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  width: 100%;
  cursor: pointer;
}
.neuro-count {
  flex: 1;
  text-align: right;
  font-size: 11px;
  color: var(--muted);
}
.neuro-chevron {
  transition: transform .2s ease;
  font-size: 10px;
}
.neuro-chevron.on {
  transform: rotate(180deg);
}

/* Items List */
.neuro-items-list {
  margin: 8px 0 0 0;
  padding-left: 0;
  list-style: none;
}
.neuro-item-detail {
  background: #f8fafc;
  border-left: 3px solid var(--accent);
  padding: 10px 12px;
  margin: 6px 0;
  border-radius: 4px;
}
.neuro-item-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.neuro-warning {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  font-weight: 600;
}
.neuro-item-standard {
  font-size: 12px;
  line-height: 1.5;
  color: var(--ink);
  margin-bottom: 4px;
  white-space: pre-line;
}
.neuro-item-tool {
  font-size: 11px;
  color: var(--muted);
  padding: 4px 8px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
  display: inline-block;
  margin-top: 4px;
}

/* 半覆盖蒙层样式 - 修改，添加按钮样式 */
.neuro-card.has-half-overlay {
  position: relative;
  overflow: hidden; /* 确保蒙层不会溢出 */
}

.neuro-half-overlay {
  position: absolute;
  top: 30%; /* 从卡片中间开始 */
  left: 0;
  width: 100%;
  height: 70%; /* 覆盖下半部分 */
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.7) 20%,
    rgba(255, 255, 255, 0.95) 100%
  );
  backdrop-filter: blur(2px);
  z-index: 5;
  border-radius: 0 0 12px 12px;
  
  /* 添加 Flexbox 布局，使按钮居中 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.neuro-half-overlay p {
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 12px;
  text-align: center;
  z-index: 6;
}

/* 完整蒙层保持不变 */
.neuro-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  z-index: 10;
  border-radius: 12px;
  /* 移除 Flexbox 相关样式，因为不需要居中按钮了 */
}

.neuro-overlay p {
  font-size: 16px;
  color: var(--muted);
  margin-bottom: 16px;
  text-align: center;
  padding: 0 20px;
}

.upgrade-btn {
  width: 144px;
  height: 36px;
  border-radius: 8px;
  font-size: 16px;
  line-height: 22px;
  padding: 0;
  border: none;
  background: #05C2CB;
  color: #fff;
  font-family: "PingFang SC";
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  z-index: 6; /* 确保按钮在最上层 */
}

.upgrade-btn:hover {
  opacity: 0.9;
}

/* Badge colors */
.domain-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid;
  font-weight: 600;
}

/* Mobile */
@media (max-width: 640px) {
  .neuro-title { font-size: 20px; }
  .neuro-card { padding: 10px; }
  .neuro-item-standard { font-size: 11px; }
  .neuro-overlay p { font-size: 14px; }
  .neuro-half-overlay p { font-size: 12px; }
}
</style>